import Table from "common/Table/Table.tsx";
import { ConsolidatedCoinData, LanguageNameSpaces } from "@shared/types";
import { TableLoadingEmpty } from "common/styles.ts";
import React, { useContext } from "react";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import { ColumnsType } from "antd/es/table";
import Flex from "common/Boxes/Flex.tsx";
import IconWithFallback from "Global/IconWithFallback.tsx";
import {
  ICON_TYPES,
  MARGIN_TYPE_TO_DISPLAY,
} from "@shared/common/constants.ts";
import Text from "common/Typography/Text.tsx";
import styled from "styled-components";
import EditOutlined from "@ant-design/icons/EditOutlined";
import SellCoinAction from "features/portfolio/Assets/SellCoinAction.tsx";
import Link from "common/Typography/Link.tsx";
import { getTableOnChangeFunction } from "common/Table/utils.ts";
import { PositionsSort } from "@shared/features/portfolio/types.ts";
import { SORT_ORDER_MAPPING } from "common/constants.ts";
import { PositionsContext } from "features/portfolio/Positions/Positions.context.ts";
import usePositionItem from "features/portfolio/common/usePositionItem.tsx";
import Title from "common/Typography/Title.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import { typography } from "@shared/common/Themes/typography.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useIsSubUser, useSelectUser } from "@shared/utils/hooks/misc.ts";
import Tooltip from "common/Tooltips/Tooltip";
import useMeasure from "utils/hooks/useMeasure.ts";
import { useTheme } from "styled-components";
import useNewLookPositionCells from "features/portfolio/NewLook/useNewLookPositionCells.tsx";

interface PositionsTableProps {
  data: ConsolidatedCoinData[];
  loading: boolean;
  emptyState?: React.JSX.Element;
}

const PositionsTable = ({ data, loading, emptyState }: PositionsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isDrawer } = useContext(DrawerContext) || {};
  const {
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    setChangeLeverageCoinData,
  } = useContext(PositionsContext)!;
  const user = useSelectUser();
  const showAccountTooltip = useMedia([`(max-width: 2000px)`], [true], false);
  const isSmallScreen = useMedia([`(max-width: 1700px)`], [true], false);
  const hidePriceChange = useMedia([`(max-width: 1550px)`], [true], false);
  const fontSize = isSmallScreen
    ? typography.fontSizes.S
    : typography.fontSizes.M;
  const isSubUser = useIsSubUser();
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const { isNewLook } = useTheme();
  const cells = useNewLookPositionCells();

  const {
    getMargin,
    getPositionSize,
    getQuantity,
    getUnrealizedPnl,
    getROI,
    getEntryPrice,
    getLiquidationPrice,
    getPrice,
    getPriceChange,
    getNewLookPriceTrend,
    openCoinExtraDetails,
    showEditLeverage,
  } = usePositionItem({ fontSize });

  const getSortOrder = (positionsSort: PositionsSort) => {
    return sortBy === positionsSort && sortDirection
      ? SORT_ORDER_MAPPING[sortDirection]
      : undefined;
  };

  const onEditClick = (e: React.MouseEvent, coinData: ConsolidatedCoinData) => {
    setChangeLeverageCoinData?.(coinData);
    e.stopPropagation();
  };

  const getFirstColumnMaxWidth = () => {
    return Math.max(
      MIN_TABLE_FIRST_COLUMN_WIDTH,
      width - MIN_TABLE_REST_COLUMNS_WIDTH
    );
  };

  const newLookColumns: ColumnsType<ConsolidatedCoinData> = [
    {
      title: t("positions.title"),
      dataIndex: "symbol",
      key: "symbol",
      showSorterTooltip: false,
      sortOrder: getSortOrder(PositionsSort.SYMBOL),
      sorter: true,
      render: (_, data) =>
        cells.symbol(data, isDrawer ? undefined : data.clients[0]),
    },
    {
      title: t("positions.direction"),
      dataIndex: "leverage",
      key: "leverage",
      showSorterTooltip: false,
      sortOrder: getSortOrder(PositionsSort.LEVERAGE),
      sorter: true,
      render: (_, data) =>
        cells.direction(
          data,
          showEditLeverage(data)
            ? () => setChangeLeverageCoinData?.(data)
            : undefined
        ),
    },
    {
      title: t("positions.size"),
      dataIndex: "position_size",
      key: "position_size",
      align: "right",
      showSorterTooltip: false,
      sortOrder: getSortOrder(PositionsSort.POSITION_SIZE),
      sorter: true,
      render: (_, data) => cells.size(data),
    },
    {
      title: t("positions.margin"),
      dataIndex: "margin",
      align: "right",
      showSorterTooltip: false,
      sortOrder: getSortOrder(PositionsSort.MARGIN),
      sorter: true,
      render: (_, data) => cells.margin(data),
    },
    {
      title: t("positions.upnl"),
      dataIndex: "unrealized_pnl",
      key: "unrealized_pnl",
      align: "right",
      showSorterTooltip: false,
      sortOrder: getSortOrder(PositionsSort.UNREALIZED_PNL),
      sorter: true,
      render: (_, data) => cells.pnl(data),
    },
    {
      title: t("positions.entry_price"),
      dataIndex: "entry_price",
      key: "entry_price",
      align: "right",
      showSorterTooltip: false,
      sortOrder: getSortOrder(PositionsSort.ENTRY_PRICE),
      sorter: true,
      render: (_, data) => cells.entry(data),
    },
    {
      title: t("positions.liq_price"),
      dataIndex: "liquidation_price",
      key: "liquidation_price",
      align: "right",
      showSorterTooltip: false,
      sortOrder: getSortOrder(PositionsSort.LIQUIDATION_PRICE),
      sorter: true,
      render: (_, data) => cells.liquidation(data),
    },
  ];

  const columns: ColumnsType<ConsolidatedCoinData> = isNewLook
    ? newLookColumns
    : [
        {
          title: t("positions.title"),
          dataIndex: "symbol",
          key: "symbol",
          showSorterTooltip: false,
          sortOrder: getSortOrder(PositionsSort.SYMBOL),
          sorter: true,
          width: showAccountTooltip ? "1px" : undefined,
          render: (symbol, data) => (
            <IconSymbolWrapper style={{ maxWidth: getFirstColumnMaxWidth() }}>
              <IconWithFallback
                iconType={ICON_TYPES.COIN}
                coinOrExchangeName={symbol}
                iconSize="xxxl"
              />
              <SymbolWrapper>
                <Text $size={fontSize}>{symbol}</Text>
                {!isDrawer && (
                  <Tooltip
                    title={data.clients[0]}
                    open={showAccountTooltip ? undefined : false}
                  >
                    <Text $ellipsis={true} $size={fontSize}>
                      {data.clients[0]}
                    </Text>
                  </Tooltip>
                )}
              </SymbolWrapper>
            </IconSymbolWrapper>
          ),
        },
        {
          title: t("positions.direction"),
          dataIndex: "leverage",
          showSorterTooltip: false,
          key: "leverage",
          sortOrder: getSortOrder(PositionsSort.LEVERAGE),
          sorter: true,
          render: (leverage, coinData) => {
            const marginTypeDisplay =
              MARGIN_TYPE_TO_DISPLAY[coinData.margin_type] || "";
            const leverageDisplay = leverage ? ` ${leverage}x` : "";
            return (
              <>
                <Text
                  type={coinData.is_short ? "danger" : "success"}
                  $size={fontSize}
                >
                  {coinData.is_short
                    ? t("positions.short")
                    : t("positions.long")}
                </Text>
                <Text type="secondary" as="div" $size={fontSize}>
                  {marginTypeDisplay}
                  {leverageDisplay}
                  {showEditLeverage(coinData) && (
                    <EditIcon
                      onClick={(e: React.MouseEvent) =>
                        onEditClick(e, coinData)
                      }
                    />
                  )}
                </Text>
              </>
            );
          },
        },
        {
          title: t("positions.size"),
          dataIndex: "position_size",
          showSorterTooltip: false,
          key: "position_size",
          sortOrder: getSortOrder(PositionsSort.POSITION_SIZE),
          sorter: true,
          render: (_, data) => (
            <>
              {getPositionSize(data)}
              {getQuantity(data)}
            </>
          ),
        },
        {
          title: t("positions.margin"),
          dataIndex: "margin",
          showSorterTooltip: false,
          sortOrder: getSortOrder(PositionsSort.MARGIN),
          sorter: true,
          render: (_, data) => getMargin(data),
        },
        {
          title: "uPnL",
          dataIndex: "unrealized_pnl",
          showSorterTooltip: false,
          key: "unrealized_pnl",
          sortOrder: getSortOrder(PositionsSort.UNREALIZED_PNL),
          sorter: true,
          render: (_, data) => (
            <PnlWrapper>
              {getUnrealizedPnl(data)}
              {getROI(data)}
            </PnlWrapper>
          ),
        },
        {
          title: (
            <div>
              <StyledTitle>{t("positions.entry_price")}</StyledTitle>
              <StyledTitle>({t("positions.price")})</StyledTitle>
            </div>
          ),
          dataIndex: "entry_price",
          showSorterTooltip: false,
          key: "entry_price",
          sortOrder: getSortOrder(PositionsSort.ENTRY_PRICE),
          sorter: true,
          render: (_, data) => (
            <div>
              {getEntryPrice(data)}
              {getPrice(data)}
            </div>
          ),
        },
        {
          title: t("positions.liq_price"),
          dataIndex: "liquidation_price",
          showSorterTooltip: false,
          key: "liquidation_price",
          sortOrder: getSortOrder(PositionsSort.LIQUIDATION_PRICE),
          sorter: true,
          render: (_, data) => getLiquidationPrice(data),
        },
      ];

  if (!hidePriceChange) {
    columns.push({
      title: t("positions.price_24h"),
      dataIndex: "graph_data",
      showSorterTooltip: false,
      sortOrder: getSortOrder(PositionsSort.CHANGE),
      sorter: true,
      width: isNewLook ? 168 : 120,
      align: isNewLook ? "right" : undefined,
      render: (_, data) =>
        isNewLook ? getNewLookPriceTrend(data) : getPriceChange(data),
    });
  }
  if (!isDrawer && !isSubUser && !user?.isWhopExperience()) {
    columns.push({
      title: "",
      dataIndex: "actions",
      key: "action",
      width: 58,
      render: (_, data) => {
        return (
          <SellCoinAction
            coinData={data}
            isPosition={true}
            trigger={<Link>{t("positions.sell")}</Link>}
          />
        );
      },
    });
  }

  const handleChange = getTableOnChangeFunction<PositionsSort>(
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    FIELD_NAME_TO_SORT
  );

  return (
    <div ref={measureRef}>
      <Table<ConsolidatedCoinData>
        columns={columns}
        dataSource={data}
        loading={loading}
        rowClassName={isDrawer ? undefined : "u-cursorPointer"}
        onRow={(coinData: ConsolidatedCoinData) => {
          return {
            onClick: () => openCoinExtraDetails?.(coinData),
          };
        }}
        onChange={handleChange}
        locale={{ emptyText: emptyState || <TableLoadingEmpty /> }}
        styles={isNewLook ? undefined : CLASSIC_TABLE_STYLES}
      />
    </div>
  );
};

export default PositionsTable;

const CLASSIC_TABLE_STYLES = {
  header: {
    cell: {
      padding: "8px 16px",
    },
  },
};

const MIN_TABLE_REST_COLUMNS_WIDTH = 850;
const MIN_TABLE_FIRST_COLUMN_WIDTH = 150;

const FIELD_NAME_TO_SORT = {
  symbol: PositionsSort.SYMBOL,
  leverage: PositionsSort.LEVERAGE,
  position_size: PositionsSort.POSITION_SIZE,
  margin: PositionsSort.MARGIN,
  unrealized_pnl: PositionsSort.UNREALIZED_PNL,
  entry_price: PositionsSort.ENTRY_PRICE,
  price: PositionsSort.PRICE,
  liquidation_price: PositionsSort.LIQUIDATION_PRICE,
  graph_data: PositionsSort.CHANGE,
};

const IconSymbolWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
`;
const SymbolWrapper = styled.div`
  overflow: hidden;
  margin-left: 12px;
`;
const EditIcon = styled(EditOutlined)`
  padding: 6px;
`;
const PnlWrapper = styled(Flex)`
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 4px;
`;
const StyledTitle = styled(Title).attrs({ level: 4 })`
  color: ${(p) => p.theme.colors.basic};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  /* New look: read like the other column headers. */
  ${(p) =>
    p.theme.isNewLook &&
    `
    &&& {
      color: inherit;
      font-size: inherit;
      font-weight: inherit;
      line-height: 1.3;
      margin: 0;
    }
  `}
`;
